import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://bloopbrasil.com.br/api'
const SANCTUM_BASE_URL = import.meta.env.VITE_SANCTUM_BASE_URL || API_BASE_URL.replace(/\/api\/?$/, '') || 'https://bloopbrasil.com.br'

/**
 * Instância ISOLADA de axios para refresh de token.
 * SEM INTERCEPTORS - para evitar loop infinito.
 */
const refreshAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN'
})

/**
 * Mutex para garantir que apenas um refresh aconteça por vez.
 * Resolve o problema de múltiplas requisições 401 disparando refresh simultâneo.
 */
let refreshPromise: Promise<boolean | null> | null = null
let isRefreshing = false

/**
 * Obtém o token CSRF do cookie
 */
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'XSRF-TOKEN') {
      try {
        return decodeURIComponent(value)
      } catch {
        return value
      }
    }
  }
  return null
}

/**
 * Faz o refresh do token usando uma instância isolada de axios.
 * Se já houver um refresh em progresso, retorna a mesma promise.
 *
 * @returns Promise<{ success: boolean, token?: string, message?: string }>
 */
export async function refreshAccessToken(): Promise<{ success: boolean; token?: string; message?: string }> {
  // Se já está fazendo refresh, retornar a mesma promise (mutex)
  if (isRefreshing && refreshPromise) {
    try {
      await refreshPromise
      // Se o refresh anterior funcionou, tentar obter o novo token do localStorage
      const newToken = localStorage.getItem('auth_token')
      if (newToken) {
        return { success: true, token: newToken }
      }
    } catch {
      // Se o refresh anterior falhou, continuar para tentar novamente
    }
  }

  isRefreshing = true

  refreshPromise = (async () => {
    const currentToken = localStorage.getItem('auth_token')

    if (!currentToken) {
      throw new Error('No token to refresh')
    }

    try {
      // Adicionar CSRF token
      const csrfToken = getCsrfToken()

      const response = await refreshAxios.post('/auth/refresh', undefined, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          ...(csrfToken ? { 'X-XSRF-TOKEN': csrfToken } : {})
        }
      })

      const newToken = response.data?.data?.token

      if (!newToken) {
        throw new Error('No token in response')
      }

      // Atualizar o token no localStorage
      localStorage.setItem('auth_token', newToken)

      return { success: true, token: newToken }
    } catch (error: any) {
      console.error('[TokenRefresh] Erro ao fazer refresh:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      })

      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao renovar token'
      }
    } finally {
      // Pequeno delay para evitar que múltiplas requisições caiam no mesmo race condition
      await new Promise(resolve => setTimeout(resolve, 100))
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Verifica se um refresh está em progresso
 */
export function isRefreshInProgress(): boolean {
  return isRefreshing
}

/**
 * Limpa o estado de refresh (útil para testes ou recovery)
 */
export function clearRefreshState(): void {
  isRefreshing = false
  refreshPromise = null
}
